import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import { 
  ArrowLeft, Info, Activity, AlertTriangle, Pill, 
  Heart, Apple, BookOpen, ExternalLink, Microscope, Sparkles, Download, Quote
} from 'lucide-react';
import jsPDF from 'jspdf';
import { conditionData } from '../data/conditionData';

// ============================================================================
// INSPIRATIONAL QUOTES
// ============================================================================

const actionQuotes = [
  { quote: "Progress, not perfection.", author: "Unknown" },
  { quote: "You're doing better than you think you are.", author: "Unknown" },
  { quote: "Small steps every day lead to big changes.", author: "Unknown" },
  { quote: "You don't have to do it all today. Just do what you can.", author: "Unknown" },
  { quote: "Every effort you make matters, even the small ones.", author: "Unknown" },
  { quote: "Be gentle with yourself. You're doing the best you can.", author: "Unknown" },
  { quote: "One day at a time. One step at a time.", author: "Unknown" },
  { quote: "Your best is enough. Your best is always enough.", author: "Unknown" },
  { quote: "Taking care of yourself isn't selfish, it's necessary.", author: "Unknown" },
  { quote: "You are not alone in this journey.", author: "Unknown" },
  { quote: "Trust yourself. You know more than you think you do.", author: "Benjamin Spock" },
  { quote: "The goal is progress, not perfection.", author: "Kathy Freston" },
  { quote: "What you're doing is harder than most people realize.", author: "Unknown" },
  { quote: "You are your child's best advocate.", author: "Unknown" },
  { quote: "Healing is not linear. Be patient with the process.", author: "Unknown" }
];

const calmingQuotes = [
  { quote: "You don't need to memorize everything. This is a reference, not a test.", author: "Unknown" },
  { quote: "Take what resonates, leave what doesn't. Trust your instincts.", author: "Unknown" },
  { quote: "Knowledge is power, but you don't need to know it all at once.", author: "Unknown" },
  { quote: "It's okay to feel overwhelmed. Take a breath and come back when you're ready.", author: "Unknown" },
  { quote: "You're not expected to be a specialist. You're a parent doing your best.", author: "Unknown" },
  { quote: "Bookmark this page. You can always come back later.", author: "Unknown" },
  { quote: "This information is here to help, not to stress you.", author: "Unknown" },
  { quote: "Learning takes time. Be patient with yourself.", author: "Unknown" },
  { quote: "You don't need to understand everything today.", author: "Unknown" },
  { quote: "Your love and presence matter more than medical knowledge.", author: "Unknown" },
  { quote: "Take breaks. This information will still be here.", author: "Unknown" },
  { quote: "It's okay to not know everything. That's why we're here to help.", author: "Unknown" },
  { quote: "One piece of information at a time. There's no rush.", author: "Unknown" },
  { quote: "You're already doing something right just by being here.", author: "Unknown" },
  { quote: "Breathe. You've got this.", author: "Unknown" }
];

// ============================================================================
// REUSABLE UI COMPONENTS
// ============================================================================

const InspirationalQuote = ({ quotes, variant = 'action' }) => {
  const randomIndex = useMemo(() => {
    const today = new Date().toDateString();
    let hash = 0;
    for (let i = 0; i < today.length; i++) {
      hash = ((hash << 5) - hash) + today.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash) % quotes.length;
  }, [quotes.length]);
  
  const randomQuote = quotes[randomIndex];
  const bgColor = variant === 'action' 
    ? 'bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20'
    : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/50';
  const iconColor = variant === 'action' ? 'text-primary' : 'text-blue-500';
  
  return (
    <div className={`${bgColor} border rounded-xl p-4 mb-6`}>
      <div className="flex items-start gap-3">
        <Quote className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div>
          <p className="text-[#2D2D2D] font-medium italic">&ldquo;{randomQuote.quote}&rdquo;</p>
          {randomQuote.author !== 'Unknown' && (
            <p className="text-sm text-[#57534E] mt-1">— {randomQuote.author}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionCard = ({ title, children, className = '', bgColor = 'bg-white' }) => (
  <Card className={`${bgColor} rounded-xl border border-stone-100 p-6 ${className}`}>
    {title && <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">{title}</h2>}
    {children}
  </Card>
);

const HighlightCard = ({ title, children, lightColor }) => (
  <Card className={`${lightColor} rounded-xl border-0 p-6`}>
    {title && <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-3">{title}</h2>}
    {children}
  </Card>
);

const BulletList = ({ items, markerColor = 'text-accent' }) => (
  <ul className="space-y-2">
    {items?.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-[#57534E]">
        <span className={`${markerColor} mt-0.5`}>•</span>
        <span>{typeof item === 'string' ? item : item.text || item.name || item}</span>
      </li>
    ))}
  </ul>
);

const NumberedList = ({ items, accentColor = 'bg-accent' }) => (
  <ol className="space-y-3">
    {items?.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className={`flex-shrink-0 w-7 h-7 ${accentColor} text-white rounded-full flex items-center justify-center text-sm font-semibold`}>
          {i + 1}
        </span>
        <span className="text-[#57534E] pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);

const InfoGrid = ({ items, columns = 2, bgColor = 'bg-stone-50' }) => (
  <div className={`grid md:grid-cols-${columns} gap-4`}>
    {items?.map((item, i) => (
      <div key={i} className={`${bgColor} rounded-lg p-4`}>
        <h3 className="font-semibold text-[#2D2D2D] mb-1">{item.name}</h3>
        <p className="text-sm text-[#57534E]">{item.description}</p>
      </div>
    ))}
  </div>
);

const WarningBox = ({ items }) => (
  <div className="space-y-3">
    {items?.map((item, i) => (
      <div key={i} className="bg-amber-50 rounded-lg p-4 flex items-start gap-3">
        <span className="text-amber-500 text-lg">⚠️</span>
        <div>
          <h4 className="font-semibold text-amber-800">{item.name}</h4>
          <p className="text-sm text-amber-900">{item.description || item.note}</p>
        </div>
      </div>
    ))}
  </div>
);

const SupplementCard = ({ item, lightColor }) => (
  <Card className="bg-white rounded-xl border border-stone-100 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
    <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">{item.name}</h3>
    <div className="space-y-3 text-sm text-[#57534E]">
      {item.dosage && (
        <p className={`${lightColor || 'bg-stone-100'} rounded-lg px-3 py-1.5 inline-block`}>
          <span className="font-semibold">Dosage: </span>{item.dosage}
        </p>
      )}
      {item.evidence && (
        <p className="text-xs text-primary font-medium">{item.evidence}</p>
      )}
      {item.description && <p>{item.description}</p>}
      {item.benefits && item.benefits.length > 0 && (
        <div>
          <p className="font-semibold text-[#2D2D2D] mb-1">Benefits:</p>
          <ul className="space-y-1 ml-1">
            {item.benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {item.note && (
        <p className="text-xs text-[#A8A29E] mt-2 italic border-t border-stone-100 pt-2">
          <span className="font-semibold text-[#57534E]">Note: </span>{item.note}
        </p>
      )}
      {item.caution && <p className="text-amber-600 italic mt-2">⚠️ {item.caution}</p>}
      {item.timing && <p><span className="font-semibold">When: </span>{item.timing}</p>}
      {item.synergy && <p><span className="font-semibold">Works with: </span>{item.synergy}</p>}
    </div>
  </Card>
);

const LifestyleCard = ({ item }) => (
  <Card className="bg-white rounded-xl border border-stone-100 p-5">
    <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">{item.name}</h3>
    <p className="text-[#57534E] text-sm leading-relaxed">{item.description}</p>
    {item.frequency && <p className="text-sm text-primary mt-2 font-medium">📅 {item.frequency}</p>}
    {item.tips && (
      <ul className="mt-3 space-y-1">
        {item.tips.map((tip, i) => (
          <li key={i} className="text-xs text-[#57534E] flex items-start gap-1">
            <span className="text-primary">→</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const DietCategoryCard = ({ title, bgColor, headerBg, textColor, items }) => (
  <Card className={`${bgColor} rounded-xl p-5`}>
    <h3 className={`${headerBg} ${textColor} rounded-lg px-4 py-2 font-bold mb-4 text-center`}>{title}</h3>
    <ul className="space-y-2">
      {items?.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-[#57534E]">
          <span className={textColor}>•</span>
          <span>{typeof item === 'object' ? item.name : item}</span>
        </li>
      ))}
    </ul>
  </Card>
);

// ============================================================================
// EMERGENCY QUICK REFERENCE CARD (MCAS)
// ============================================================================

const EmergencyQuickRefCard = ({ data, onExportPDF }) => {
  if (!data) return null;
  
  return (
    <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 print:break-inside-avoid">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-nunito font-bold text-red-800">{data.title}</h2>
            <p className="text-sm text-red-600">{data.subtitle}</p>
          </div>
        </div>
        <div className="flex gap-2 print:hidden">
          <button 
            onClick={onExportPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
            data-testid="download-emergency-pdf"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
          <button 
            onClick={() => window.print()} 
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        {/* Pre-Medication Protocol */}
        {data.preMedicationProtocol && (
          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-bold text-red-700 mb-2">{data.preMedicationProtocol.title}</h3>
            <p className="text-xs text-red-600 mb-2 italic">{data.preMedicationProtocol.subtitle}</p>
            <ol className="space-y-1">
              {data.preMedicationProtocol.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="font-bold text-red-600">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* EpiPen Instructions */}
        {data.epiPenInstructions && (
          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-bold text-red-700 mb-2">{data.epiPenInstructions.title}</h3>
            <p className="text-xs font-semibold text-red-600 mb-1">WHEN TO USE:</p>
            <ul className="space-y-0.5 mb-2">
              {data.epiPenInstructions.whenToUse.map((item, i) => (
                <li key={i} className="flex items-start gap-1 text-gray-700">
                  <span className="text-red-500">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs font-semibold text-red-600 mb-1">HOW TO USE:</p>
            <ol className="space-y-0.5">
              {data.epiPenInstructions.howToUse.map((step, i) => (
                <li key={i} className="text-gray-700">{step}</li>
              ))}
            </ol>
          </div>
        )}

        {/* Emergency Med Kit */}
        {data.emergencyMeds && (
          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-bold text-red-700 mb-2">{data.emergencyMeds.title}</h3>
            <ul className="space-y-1">
              {data.emergencyMeds.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500">☐</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tell ER Staff */}
        {data.tellER && (
          <div className="bg-white/80 rounded-lg p-4">
            <h3 className="font-bold text-red-700 mb-2">{data.tellER.title}</h3>
            <ul className="space-y-1">
              {data.tellER.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-500">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Emergency Contacts */}
      {data.emergencyContacts && (
        <div className="mt-4 bg-white/80 rounded-lg p-4">
          <h3 className="font-bold text-red-700 mb-1">{data.emergencyContacts.title}</h3>
          <p className="text-xs text-gray-500 italic mb-2">{data.emergencyContacts.note}</p>
          <div className="font-mono text-sm text-gray-700 space-y-1">
            {data.emergencyContacts.fields.map((field, i) => (
              <p key={i}>{field}</p>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

// ============================================================================
// TAB CONTENT COMPONENTS
// ============================================================================

const AboutTab = ({ condition, onExportPDF }) => {
  // Handle MCAS-style with emergency card
  if (condition.about?.isExtendedMCAS) {
    return (
      <div className="space-y-6">
        {condition.emergencyQuickRef && (
          <EmergencyQuickRefCard data={condition.emergencyQuickRef} onExportPDF={onExportPDF} />
        )}
        
        {condition.about.whatIs && (
          <SectionCard title={condition.about.whatIs.title}>
            <p className="text-[#57534E] leading-relaxed">{condition.about.whatIs.description}</p>
          </SectionCard>
        )}

        {condition.about.coreConcept && (
          <HighlightCard title={condition.about.coreConcept.title} lightColor={condition.lightColor}>
            <p className="text-[#57534E] leading-relaxed mb-4">{condition.about.coreConcept.description}</p>
            {condition.about.coreConcept.comparison && (
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-bold text-green-700 mb-2">Normal Response</h4>
                  <p className="text-[#57534E]">{condition.about.coreConcept.comparison.normal}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-bold text-red-700 mb-2">In MCAS</h4>
                  <p className="text-[#57534E]">{condition.about.coreConcept.comparison.mcas}</p>
                </div>
              </div>
            )}
          </HighlightCard>
        )}

        {condition.about.whyItHappens && (
          <SectionCard title={condition.about.whyItHappens.title}>
            <p className="text-[#57534E] mb-4">{condition.about.whyItHappens.description}</p>
            {condition.about.whyItHappens.factors && <InfoGrid items={condition.about.whyItHappens.factors} columns={1} />}
          </SectionCard>
        )}

        {condition.about.keyStats && (
          <SectionCard title={condition.about.keyStats.title}>
            <div className="grid md:grid-cols-3 gap-4">
              {condition.about.keyStats.stats.map((stat, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{stat.value}</p>
                  <p className="text-sm text-[#57534E]">{stat.label}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {condition.about.pansConnection && (
          <Card className="bg-accent/10 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">{condition.about.pansConnection.title}</h2>
            <p className="text-[#57534E] mb-4">{condition.about.pansConnection.description}</p>
            {condition.about.pansConnection.points && <BulletList items={condition.about.pansConnection.points} />}
          </Card>
        )}

        {/* Histamine-specific sections */}
        {condition.about.whatIsHistamine && (
          <HighlightCard title={condition.about.whatIsHistamine.title} lightColor={condition.lightColor}>
            <p className="text-[#57534E] mb-4">{condition.about.whatIsHistamine.intro}</p>
            <div className="space-y-3">
              {condition.about.whatIsHistamine.roles.map((role, i) => (
                <div key={i} className="bg-white/60 rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2D2D] mb-1">{role.name}</h3>
                  <p className="text-sm text-[#57534E]">{role.description}</p>
                </div>
              ))}
            </div>
          </HighlightCard>
        )}

        {condition.about.pansProblem && (
          <SectionCard title={condition.about.pansProblem.title}>
            <p className="text-[#57534E] mb-4">{condition.about.pansProblem.intro}</p>
            <BulletList items={condition.about.pansProblem.reasons} markerColor="text-secondary" />
          </SectionCard>
        )}

        {condition.about.breakdown && (
          <SectionCard title={condition.about.breakdown.title}>
            <p className="text-[#57534E] mb-4">{condition.about.breakdown.intro}</p>
            <div className="space-y-4">
              {condition.about.breakdown.enzymes.map((enzyme, i) => (
                <div key={i} className="bg-secondary/10 rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2D2D] mb-1">{i + 1}. {enzyme.name}</h3>
                  <p className="text-sm text-[#57534E]">{enzyme.description}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {condition.about.bucketTheory && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{condition.about.bucketTheory.title}</h2>
            <p className="text-amber-900 mb-4">{condition.about.bucketTheory.intro}</p>
            <BulletList items={condition.about.bucketTheory.points} markerColor="text-amber-600" />
          </Card>
        )}

        {condition.about.receptors && (
          <SectionCard title={condition.about.receptors.title}>
            <p className="text-[#57534E] mb-4">{condition.about.receptors.intro}</p>
            <InfoGrid items={condition.about.receptors.types} bgColor="bg-secondary/10" />
          </SectionCard>
        )}

        {condition.about.causes && (
          <SectionCard title={condition.about.causes.title}>
            <BulletList items={condition.about.causes.items} markerColor="text-secondary" />
          </SectionCard>
        )}

        {condition.about.testing && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{condition.about.testing.title}</h2>
            <p className="text-amber-900 mb-4">{condition.about.testing.intro}</p>
            <WarningBox items={condition.about.testing.challenges} />
          </Card>
        )}
      </div>
    );
  }

  // Handle Histamine Extended style
  if (condition.about?.isExtendedHistamine) {
    return (
      <div className="space-y-6">
        {condition.about.whatIs && (
          <SectionCard title={condition.about.whatIs.title}>
            <p className="text-[#57534E] leading-relaxed">{condition.about.whatIs.description}</p>
          </SectionCard>
        )}

        {condition.about.whatIsHistamine && (
          <HighlightCard title={condition.about.whatIsHistamine.title} lightColor={condition.lightColor}>
            <p className="text-[#57534E] mb-4">{condition.about.whatIsHistamine.intro}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {condition.about.whatIsHistamine.roles?.map((role, i) => (
                <div key={i} className="bg-white/60 rounded-lg p-4">
                  <h3 className="font-semibold text-[#2D2D2D] mb-1">{role.name}</h3>
                  <p className="text-sm text-[#57534E]">{role.description}</p>
                </div>
              ))}
            </div>
          </HighlightCard>
        )}

        {condition.about.pansProblem && (
          <Card className="bg-accent/10 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">{condition.about.pansProblem.title}</h2>
            <p className="text-[#57534E] mb-4">{condition.about.pansProblem.intro}</p>
            <BulletList items={condition.about.pansProblem.reasons} />
          </Card>
        )}

        {condition.about.breakdown && (
          <SectionCard title={condition.about.breakdown.title}>
            <p className="text-[#57534E] mb-4">{condition.about.breakdown.intro}</p>
            <div className="space-y-3">
              {condition.about.breakdown.enzymes?.map((enzyme, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#2D2D2D] mb-1">{enzyme.name}</h4>
                  <p className="text-sm text-[#57534E]">{enzyme.description}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {condition.about.bucketTheory && (
          <HighlightCard title={condition.about.bucketTheory.title} lightColor="bg-amber-50">
            <p className="text-[#57534E] mb-4">{condition.about.bucketTheory.intro}</p>
            <BulletList items={condition.about.bucketTheory.points} />
          </HighlightCard>
        )}

        {condition.about.receptors && (
          <SectionCard title={condition.about.receptors.title}>
            <p className="text-[#57534E] mb-4">{condition.about.receptors.intro}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {condition.about.receptors.types?.map((type, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-3">
                  <h4 className="font-semibold text-[#2D2D2D] mb-1">{type.name}</h4>
                  <p className="text-sm text-[#57534E]">{type.effects}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {condition.about.causes && (
          <SectionCard title={condition.about.causes.title}>
            <BulletList items={condition.about.causes.items} />
          </SectionCard>
        )}

        {condition.about.testing && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{condition.about.testing.title}</h2>
            <p className="text-amber-900 mb-4">{condition.about.testing.intro}</p>
            <WarningBox items={condition.about.testing.challenges} />
          </Card>
        )}
      </div>
    );
  }

  // Handle Extended style (Mycoplasma)
  if (condition.about?.isExtended) {
    return (
      <div className="space-y-6">
        {condition.about.whatIs && (
          <SectionCard title={condition.about.whatIs.title}>
            <p className="text-[#57534E] leading-relaxed">{condition.about.whatIs.description}</p>
          </SectionCard>
        )}

        {condition.about.whyItMatters && (
          <HighlightCard title={condition.about.whyItMatters.title} lightColor={condition.lightColor}>
            <p className="text-[#57534E] leading-relaxed">{condition.about.whyItMatters.description}</p>
          </HighlightCard>
        )}

        {condition.about.uniqueCharacteristics && (
          <SectionCard title={condition.about.uniqueCharacteristics.title}>
            <InfoGrid items={condition.about.uniqueCharacteristics.points} columns={1} />
          </SectionCard>
        )}

        {condition.about.howItTriggersPans && (
          <Card className="bg-accent/10 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">{condition.about.howItTriggersPans.title}</h2>
            <NumberedList items={condition.about.howItTriggersPans.steps} />
          </Card>
        )}

        {condition.about.testingChallenges && (
          <SectionCard title={condition.about.testingChallenges.title}>
            <p className="text-[#57534E] mb-4">{condition.about.testingChallenges.description}</p>
            <WarningBox items={condition.about.testingChallenges.challenges} />
          </SectionCard>
        )}

        {condition.about.prevalence && (
          <SectionCard title={condition.about.prevalence.title}>
            <BulletList items={condition.about.prevalence.points} />
          </SectionCard>
        )}

        {condition.about.exposureRoutes && (
          <SectionCard title={condition.about.exposureRoutes.title}>
            <BulletList items={condition.about.exposureRoutes.items} />
          </SectionCard>
        )}

        {condition.about.clinicalObservations && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-green-800 mb-3">{condition.about.clinicalObservations.title}</h2>
            {condition.about.clinicalObservations.intro && (
              <p className="text-green-900 mb-3">{condition.about.clinicalObservations.intro}</p>
            )}
            <ul className="space-y-2">
              {condition.about.clinicalObservations.items?.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-green-800">
                  <span className="text-green-500 mt-0.5">✓</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // Handle MTHFR Extended style
  if (condition.about?.whatIsMethylation) {
    return (
      <div className="space-y-6">
        {/* Overview */}
        {condition.about.overview && (
          <SectionCard title="Understanding Methylation & Detoxification">
            <p className="text-[#57534E] leading-relaxed">{condition.about.overview}</p>
          </SectionCard>
        )}

        {/* What is Methylation */}
        {condition.about.whatIsMethylation && (
          <HighlightCard title={condition.about.whatIsMethylation.title} lightColor={condition.lightColor}>
            <p className="text-[#57534E] mb-4">{condition.about.whatIsMethylation.description}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {condition.about.whatIsMethylation.effects.map((effect, i) => (
                <div key={i} className="bg-white/60 rounded-lg p-3">
                  <h4 className="font-semibold text-[#2D2D2D] mb-1">{effect.name}</h4>
                  <p className="text-sm text-[#57534E]">{effect.description}</p>
                </div>
              ))}
            </div>
          </HighlightCard>
        )}

        {/* MTHFR Gene Section */}
        {condition.about.mthfrGene && (
          <SectionCard title={condition.about.mthfrGene.title}>
            <p className="text-[#57534E] mb-4">{condition.about.mthfrGene.whatIs}</p>
            
            {condition.about.mthfrGene.mutations && (
              <div className="space-y-4 mt-4">
                <h3 className="font-semibold text-[#2D2D2D]">{condition.about.mthfrGene.mutations.intro}</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="bg-blue-50 border-blue-200 p-4">
                    <h4 className="font-bold text-blue-800 mb-2">{condition.about.mthfrGene.mutations.c677t.name}</h4>
                    <ul className="space-y-1 text-sm text-blue-900">
                      <li><strong>Heterozygous:</strong> {condition.about.mthfrGene.mutations.c677t.heterozygous}</li>
                      <li><strong>Homozygous:</strong> {condition.about.mthfrGene.mutations.c677t.homozygous}</li>
                    </ul>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200 p-4">
                    <h4 className="font-bold text-purple-800 mb-2">{condition.about.mthfrGene.mutations.a1298c.name}</h4>
                    <ul className="space-y-1 text-sm text-purple-900">
                      <li><strong>Heterozygous:</strong> {condition.about.mthfrGene.mutations.a1298c.heterozygous}</li>
                      <li><strong>Homozygous:</strong> {condition.about.mthfrGene.mutations.a1298c.homozygous}</li>
                      <li><strong>Compound:</strong> {condition.about.mthfrGene.mutations.a1298c.compound}</li>
                    </ul>
                  </Card>
                </div>
              </div>
            )}

            {condition.about.mthfrGene.impairmentEffects && (
              <Card className="bg-amber-50 border-amber-200 p-4 mt-4">
                <h4 className="font-bold text-amber-800 mb-3">What Happens When MTHFR is Impaired?</h4>
                <ul className="space-y-2">
                  {condition.about.mthfrGene.impairmentEffects.map((effect, i) => (
                    <li key={i} className="flex items-start gap-2 text-amber-900 text-sm">
                      <span className="text-amber-500 mt-0.5">⚠️</span>
                      <span>{effect}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </SectionCard>
        )}

        {/* COMT Gene Section */}
        {condition.about.comtGene && (
          <SectionCard title={condition.about.comtGene.title}>
            <p className="text-[#57534E] mb-4">{condition.about.comtGene.whatIs}</p>
            
            {condition.about.comtGene.variations && (
              <div className="space-y-4">
                <p className="text-[#57534E] font-medium">{condition.about.comtGene.variations.intro}</p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <Card className="bg-green-50 border-green-200 p-4">
                    <h4 className="font-bold text-green-800 mb-2">{condition.about.comtGene.variations.valval.name}</h4>
                    <ul className="space-y-1 text-sm text-green-900">
                      <li><strong>Activity:</strong> {condition.about.comtGene.variations.valval.activity}</li>
                      <li><strong>Result:</strong> {condition.about.comtGene.variations.valval.result}</li>
                      <li><strong>Personality:</strong> {condition.about.comtGene.variations.valval.personality}</li>
                      <li><strong>Downsides:</strong> {condition.about.comtGene.variations.valval.downsides}</li>
                    </ul>
                  </Card>
                  <Card className="bg-red-50 border-red-200 p-4">
                    <h4 className="font-bold text-red-800 mb-2">{condition.about.comtGene.variations.metmet.name}</h4>
                    <ul className="space-y-1 text-sm text-red-900">
                      <li><strong>Activity:</strong> {condition.about.comtGene.variations.metmet.activity}</li>
                      <li><strong>Result:</strong> {condition.about.comtGene.variations.metmet.result}</li>
                      <li><strong>Personality:</strong> {condition.about.comtGene.variations.metmet.personality}</li>
                      <li><strong>Downsides:</strong> {condition.about.comtGene.variations.metmet.downsides}</li>
                    </ul>
                  </Card>
                  <Card className="bg-yellow-50 border-yellow-200 p-4">
                    <h4 className="font-bold text-yellow-800 mb-2">{condition.about.comtGene.variations.valmet.name}</h4>
                    <p className="text-sm text-yellow-900">{condition.about.comtGene.variations.valmet.description}</p>
                  </Card>
                </div>
              </div>
            )}

            {condition.about.comtGene.pandasRelevance && (
              <Card className="bg-accent/10 border-0 p-4 mt-4">
                <h4 className="font-bold text-[#2D2D2D] mb-3">Why COMT Matters in PANDAS/PANS</h4>
                <ul className="space-y-2">
                  {condition.about.comtGene.pandasRelevance.map((point, i) => (
                    <li key={i} className="flex items-start gap-2 text-[#57534E] text-sm">
                      <span className="text-accent mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </SectionCard>
        )}

        {/* Other Genes Section */}
        {condition.about.otherGenes && (
          <SectionCard title={condition.about.otherGenes.title}>
            <div className="space-y-4">
              {condition.about.otherGenes.genes.map((gene, i) => (
                <Card key={i} className="bg-stone-50 border-stone-200 p-4">
                  <h4 className="font-bold text-[#2D2D2D] mb-2">{gene.name}</h4>
                  <p className="text-sm text-[#57534E] mb-2"><strong>Function:</strong> {gene.function}</p>
                  {gene.whenImpaired && <p className="text-sm text-[#57534E] mb-1"><strong>When impaired:</strong> {gene.whenImpaired}</p>}
                  {gene.upregulation && <p className="text-sm text-[#57534E] mb-1"><strong>Upregulation:</strong> {gene.upregulation}</p>}
                  {gene.management && <p className="text-sm text-[#57534E] mb-1"><strong>Management:</strong> {gene.management}</p>}
                  {gene.support && <p className="text-sm text-[#57534E] mb-1"><strong>Support:</strong> {gene.support}</p>}
                  {gene.slowMAOA && <p className="text-sm text-[#57534E] mb-1"><strong>Slow MAO-A:</strong> {gene.slowMAOA}</p>}
                  {gene.fastMAOA && <p className="text-sm text-[#57534E]"><strong>Fast MAO-A:</strong> {gene.fastMAOA}</p>}
                </Card>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Detoxification Section */}
        {condition.about.detoxification && (
          <SectionCard title={condition.about.detoxification.title}>
            <p className="text-[#57534E] mb-4">{condition.about.detoxification.intro}</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Card className="bg-blue-50 border-blue-200 p-4">
                <h4 className="font-bold text-blue-800 mb-2">{condition.about.detoxification.phaseI.title}</h4>
                <ul className="space-y-1">
                  {condition.about.detoxification.phaseI.points.map((point, i) => (
                    <li key={i} className="text-sm text-blue-900 flex items-start gap-2">
                      <span className="text-blue-500 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              <Card className="bg-green-50 border-green-200 p-4">
                <h4 className="font-bold text-green-800 mb-2">{condition.about.detoxification.phaseII.title}</h4>
                <ul className="space-y-1">
                  {condition.about.detoxification.phaseII.points.map((point, i) => (
                    <li key={i} className="text-sm text-green-900 flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>

            {condition.about.detoxification.whenWrong && (
              <Card className="bg-red-50 border-red-200 p-4">
                <h4 className="font-bold text-red-800 mb-2">When Detox Goes Wrong</h4>
                <ul className="space-y-2">
                  {condition.about.detoxification.whenWrong.map((item, i) => (
                    <li key={i} className="text-sm text-red-900 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">⚠️</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </SectionCard>
        )}

        {/* PANDAS Connection Section */}
        {condition.about.pandasConnection && (
          <Card className="bg-accent/10 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">{condition.about.pandasConnection.title}</h2>
            <p className="text-[#57534E] font-medium mb-4">{condition.about.pandasConnection.intro}</p>
            <ul className="space-y-2 mb-4">
              {condition.about.pandasConnection.points.map((point, i) => (
                <li key={i} className="flex items-start gap-2 text-[#57534E]">
                  <span className="text-accent mt-0.5">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
            
            {condition.about.pandasConnection.commonScenario && (
              <Card className="bg-amber-50 border-amber-200 p-4">
                <h4 className="font-bold text-amber-800 mb-2">{condition.about.pandasConnection.commonScenario.title}</h4>
                <p className="text-sm text-amber-900">{condition.about.pandasConnection.commonScenario.description}</p>
              </Card>
            )}
          </Card>
        )}
      </div>
    );
  }

  // Standard PANDAS-style about
  return (
    <div className="space-y-6">
      {condition.about.overview && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          <div className="prose prose-stone max-w-none">
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mt-6 mb-3">{children}</h2>,
                h3: ({children}) => <h3 className="text-lg font-semibold text-[#2D2D2D] mt-4 mb-2">{children}</h3>,
                p: ({children}) => <p className="text-[#57534E] leading-relaxed mb-4">{children}</p>,
                strong: ({children}) => <strong className="font-semibold text-[#2D2D2D]">{children}</strong>,
                em: ({children}) => <em className="italic text-[#57534E]">{children}</em>,
                ul: ({children}) => <ul className="space-y-2 my-4">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal list-inside space-y-2 my-4">{children}</ol>,
                li: ({children}) => (
                  <li className="flex items-start gap-2 text-[#57534E]">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{children}</span>
                  </li>
                ),
                hr: () => <hr className="my-6 border-stone-200" />,
              }}
            >
              {condition.about.overview}
            </ReactMarkdown>
          </div>
        </Card>
      )}

      {condition.about.keyPoints && (
        <HighlightCard title="Key Points" lightColor={condition.lightColor}>
          <BulletList items={condition.about.keyPoints} markerColor={condition.textColor} />
        </HighlightCard>
      )}

      {condition.about.prevalence && typeof condition.about.prevalence === 'string' && (
        <SectionCard title="Prevalence">
          <p className="text-[#57534E]">{condition.about.prevalence}</p>
        </SectionCard>
      )}

      {condition.about.ageOfOnset && (
        <SectionCard title="Age of Onset">
          <p className="text-[#57534E]">{condition.about.ageOfOnset}</p>
        </SectionCard>
      )}
    </div>
  );
};

const SymptomsTab = ({ condition }) => {
  const symptoms = condition.symptoms;
  if (!symptoms) return <p className="text-[#57534E]">No symptoms information available.</p>;

  // Histamine Extended Symptoms
  if (symptoms.isExtendedHistamine) {
    return (
      <div className="space-y-6">
        {symptoms.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <p className="text-[#57534E] leading-relaxed">{symptoms.intro}</p>
          </Card>
        )}

        {symptoms.categories?.map((category, idx) => (
          <SectionCard key={idx} title={`${category.icon || ''} ${category.name}`}>
            <div className="grid md:grid-cols-2 gap-3">
              {category.items?.map((item, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-3">
                  <span className="text-[#57534E]">{typeof item === 'string' ? item : item.name}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        ))}

        {symptoms.importantNotes && (
          <Card className="bg-amber-50 rounded-xl border border-amber-200 p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-4">Important Notes</h2>
            <ul className="space-y-2">
              {symptoms.importantNotes.map((note, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-900">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // MTHFR Extended Symptoms
  if (symptoms.mthfrSymptoms) {
    return (
      <div className="space-y-6">
        {/* Intro */}
        {symptoms.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <p className="text-[#57534E] leading-relaxed">{symptoms.intro}</p>
          </Card>
        )}

        {/* MTHFR Mutation Symptoms */}
        {symptoms.mthfrSymptoms && (
          <SectionCard title={symptoms.mthfrSymptoms.title}>
            <div className="space-y-4">
              {symptoms.mthfrSymptoms.categories.map((category, idx) => (
                <Card key={idx} className="bg-white border border-stone-100 p-4">
                  <h4 className="font-bold text-[#2D2D2D] mb-3">{category.name}</h4>
                  <div className="grid md:grid-cols-2 gap-2">
                    {category.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-primary mt-0.5">•</span>
                        <span className="text-[#57534E]">
                          <strong>{item.name}</strong>
                          {item.description && ` - ${item.description}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Slow COMT Symptoms */}
        {symptoms.slowCOMT && (
          <Card className="bg-red-50 border-red-200 rounded-xl p-6">
            <h3 className="text-lg font-nunito font-bold text-red-800 mb-4">{symptoms.slowCOMT.title}</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {symptoms.slowCOMT.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span className="text-red-900">
                    <strong>{item.name}</strong>
                    {item.description && ` - ${item.description}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Fast COMT Symptoms */}
        {symptoms.fastCOMT && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-6">
            <h3 className="text-lg font-nunito font-bold text-green-800 mb-4">{symptoms.fastCOMT.title}</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {symptoms.fastCOMT.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-green-500 mt-0.5">•</span>
                  <span className="text-green-900">
                    <strong>{item.name}</strong>
                    {item.description && ` - ${item.description}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* CBS Upregulation Symptoms */}
        {symptoms.cbsUpregulation && (
          <Card className="bg-purple-50 border-purple-200 rounded-xl p-6">
            <h3 className="text-lg font-nunito font-bold text-purple-800 mb-4">{symptoms.cbsUpregulation.title}</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {symptoms.cbsUpregulation.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-purple-500 mt-0.5">•</span>
                  <span className="text-purple-900">
                    <strong>{item.name}</strong>
                    {item.description && ` - ${item.description}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* GST Defects Symptoms */}
        {symptoms.gstDefects && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h3 className="text-lg font-nunito font-bold text-amber-800 mb-4">{symptoms.gstDefects.title}</h3>
            <div className="grid md:grid-cols-2 gap-2">
              {symptoms.gstDefects.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span className="text-amber-900">
                    <strong>{item.name}</strong>
                    {item.description && ` - ${item.description}`}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Key Point */}
        {symptoms.keyPoint && (
          <Card className="bg-accent/20 border-accent/30 rounded-xl p-6">
            <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">Key Point</h3>
            <p className="text-[#57534E] font-medium">{symptoms.keyPoint}</p>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview text */}
      {symptoms.overview && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          {symptoms.overview.split('\n\n').map((paragraph, idx) => {
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return <h2 key={idx} className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">{paragraph.replace(/\*\*/g, '')}</h2>;
            }
            return <p key={idx} className="text-[#57534E] leading-relaxed">{paragraph}</p>;
          })}
        </Card>
      )}

      {/* Primary symptoms (PANDAS/Mold style) */}
      {symptoms.primary && symptoms.primary.map((symptom, idx) => (
        <SectionCard key={idx} title={symptom.name}>
          {symptom.description && <p className="text-[#57534E] mb-4">{symptom.description}</p>}
          {symptom.details && (
            <ul className="space-y-2">
              {symptom.details.map((detail, i) => (
                <li key={i} className="flex items-start gap-2 text-[#57534E]">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{detail}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      ))}

      {/* Secondary overview text */}
      {symptoms.secondaryOverview && (
        <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mt-8">{symptoms.secondaryOverview.replace(/\*\*/g, '')}</h2>
      )}

      {/* Secondary symptoms */}
      {symptoms.secondary && (
        <div className="grid md:grid-cols-2 gap-4">
          {symptoms.secondary.map((symptom, idx) => (
            <Card key={idx} className="bg-white rounded-xl border border-stone-100 p-5">
              <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-3">{symptom.name}</h3>
              {symptom.description && <p className="text-sm text-[#57534E] mb-3">{symptom.description}</p>}
              {symptom.details && (
                <ul className="space-y-1.5">
                  {symptom.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#57534E]">
                      <span className="text-accent mt-0.5">•</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Important Notes (for Mold/CIRS) */}
      {symptoms.importantNotes && (
        <Card className="bg-amber-50 rounded-xl border border-amber-200 p-6 mt-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-4">
            {symptoms.importantNotes.title}
          </h2>
          <ul className="space-y-2">
            {symptoms.importantNotes.notes.map((note, i) => (
              <li key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Existing category-based format */}
      {symptoms.categories?.map((category, idx) => (
        <SectionCard key={idx} title={category.name}>
          {category.description && <p className="text-[#57534E] mb-4">{category.description}</p>}
          {category.items && (
            <div className="grid md:grid-cols-2 gap-3">
              {category.items.map((item, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-3">
                  <span className="text-[#57534E]">{typeof item === 'string' ? item : item.name}</span>
                  {typeof item === 'object' && item.note && (
                    <p className="text-xs text-[#A8A29E] mt-1">{item.note}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      ))}

      {symptoms.core && (
        <SectionCard title={symptoms.core.title || 'Core Symptoms'}>
          <BulletList items={symptoms.core.items || symptoms.core} />
        </SectionCard>
      )}

      {symptoms.associated && (
        <SectionCard title={symptoms.associated.title || 'Associated Symptoms'}>
          <BulletList items={symptoms.associated.items || symptoms.associated} />
        </SectionCard>
      )}

      {symptoms.redFlags && (
        <Card className="bg-red-50 rounded-xl border border-red-200 p-6">
          <h2 className="text-xl font-nunito font-bold text-red-700 mb-4">
            {symptoms.redFlags.title || 'Red Flags - Seek Immediate Care'}
          </h2>
          <WarningBox items={(symptoms.redFlags.items || symptoms.redFlags).map(item => 
            typeof item === 'string' ? { name: item, description: '' } : item
          )} />
        </Card>
      )}

      {symptoms.patterns && (
        <SectionCard title={symptoms.patterns.title || 'Symptom Patterns'}>
          <InfoGrid items={symptoms.patterns.items || symptoms.patterns} columns={1} />
        </SectionCard>
      )}

      {symptoms.pansOverlap && (
        <HighlightCard title={symptoms.pansOverlap.title || 'Overlap with PANS'} lightColor={condition.lightColor}>
          <p className="text-[#57534E] mb-4">{symptoms.pansOverlap.description}</p>
          {symptoms.pansOverlap.items && <BulletList items={symptoms.pansOverlap.items} />}
        </HighlightCard>
      )}
    </div>
  );
};

const TriggersTab = ({ condition }) => {
  const triggers = condition.triggers;
  if (!triggers) return <p className="text-[#57534E]">No triggers information available.</p>;

  // Histamine Extended Triggers/Testing
  if (triggers.isExtendedHistamine) {
    return (
      <div className="space-y-6">
        {triggers.howItAccumulates && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-4">{triggers.howItAccumulates.title}</h2>
            <NumberedList items={triggers.howItAccumulates.steps} />
          </Card>
        )}

        {triggers.factorsIncrease && (
          <SectionCard title={triggers.factorsIncrease.title}>
            <BulletList items={triggers.factorsIncrease.items} />
          </SectionCard>
        )}

        {triggers.medications && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{triggers.medications.title}</h2>
            <p className="text-amber-900 mb-4">{triggers.medications.intro}</p>
            <ul className="space-y-2">
              {triggers.medications.items?.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-900">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {triggers.medications.warning && (
              <p className="mt-4 text-sm text-amber-800 font-medium bg-amber-100 p-3 rounded-lg">{triggers.medications.warning}</p>
            )}
          </Card>
        )}

        {triggers.environmental && (
          <SectionCard title={triggers.environmental.title}>
            <BulletList items={triggers.environmental.items} />
          </SectionCard>
        )}
      </div>
    );
  }

  // MTHFR-specific Testing tab
  if (triggers.geneticTesting) {
    return (
      <div className="space-y-6">
        {/* Intro */}
        {triggers.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">Testing for Methylation & Detox Gene Variants</h2>
            <p className="text-[#57534E] leading-relaxed">{triggers.intro}</p>
          </Card>
        )}

        {/* Genetic Testing Options */}
        {triggers.geneticTesting && (
          <SectionCard title={triggers.geneticTesting.title}>
            {triggers.geneticTesting.options?.map((opt, i) => (
              <Card key={i} className="bg-blue-50 border-blue-200 p-4 mb-4">
                <h4 className="font-bold text-blue-800 mb-2">{opt.name}</h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm text-blue-900">
                  <p><strong>Cost:</strong> {opt.cost}</p>
                  <p><strong>What you get:</strong> {opt.whatYouGet}</p>
                  <p><strong>Next step:</strong> {opt.nextStep}</p>
                  <p><strong>Pros:</strong> {opt.pros}</p>
                  <p><strong>Cons:</strong> {opt.cons}</p>
                </div>
              </Card>
            ))}

            {/* Interpretation Sites */}
            {triggers.geneticTesting.interpretationSites && (
              <div className="mt-4">
                <h3 className="font-bold text-[#2D2D2D] mb-3">{triggers.geneticTesting.interpretationSites.title}</h3>
                <p className="text-[#57534E] mb-3">{triggers.geneticTesting.interpretationSites.intro}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {triggers.geneticTesting.interpretationSites.sites.map((site, i) => (
                    <Card key={i} className="bg-green-50 border-green-200 p-3">
                      <h4 className="font-semibold text-green-800">{site.name}</h4>
                      <p className="text-sm text-green-700">Cost: {site.cost}</p>
                      <p className="text-sm text-green-900">{site.description}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Medical Grade Testing */}
            {triggers.geneticTesting.medicalGrade && (
              <div className="mt-4">
                <h3 className="font-bold text-[#2D2D2D] mb-2">{triggers.geneticTesting.medicalGrade.title}</h3>
                <ul className="space-y-1">
                  {triggers.geneticTesting.medicalGrade.items.map((item, i) => (
                    <li key={i} className="text-[#57534E] flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Direct MTHFR Testing */}
            {triggers.geneticTesting.directMTHFR && (
              <Card className="bg-purple-50 border-purple-200 p-4 mt-4">
                <h4 className="font-bold text-purple-800 mb-2">{triggers.geneticTesting.directMTHFR.title}</h4>
                <div className="space-y-1 text-sm text-purple-900">
                  {triggers.geneticTesting.directMTHFR.items.map((item, i) => (
                    <p key={i}><strong>{item.label}:</strong> {item.value}</p>
                  ))}
                </div>
              </Card>
            )}
          </SectionCard>
        )}

        {/* Key Genes to Look For */}
        {triggers.keyGenes && (
          <SectionCard title={triggers.keyGenes.title}>
            <div className="grid md:grid-cols-2 gap-2">
              {triggers.keyGenes.genes.map((gene, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-2 text-sm text-[#57534E]">
                  {gene}
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Functional Testing */}
        {triggers.functionalTesting && (
          <SectionCard title={triggers.functionalTesting.title}>
            <p className="text-[#57534E] mb-4 font-medium">{triggers.functionalTesting.intro}</p>
            <div className="space-y-4">
              {triggers.functionalTesting.tests.map((test, i) => (
                <Card key={i} className="bg-amber-50 border-amber-200 p-4">
                  <h4 className="font-bold text-amber-800 mb-2">{test.name}</h4>
                  <div className="text-sm text-amber-900 space-y-1">
                    {test.optimal && <p><strong>Optimal:</strong> {test.optimal}</p>}
                    {test.elevated && <p><strong>Elevated:</strong> {test.elevated}</p>}
                    {test.meaning && <p><strong>What it means:</strong> {test.meaning}</p>}
                    {test.veryLow && <p><strong>Very low:</strong> {test.veryLow}</p>}
                    {test.whatItShows && <p><strong>What it shows:</strong> {test.whatItShows}</p>}
                    {test.description && <p>{test.description}</p>}
                    {test.providers && <p><strong>Providers:</strong> {test.providers}</p>}
                    {test.cost && <p><strong>Cost:</strong> {test.cost}</p>}
                    {test.helps && <p><strong>Helps understand:</strong> {test.helps}</p>}
                    {test.note && <p><strong>Note:</strong> {test.note}</p>}
                    {test.blood && <p><strong>Blood:</strong> {test.blood}</p>}
                    {test.important && <p><strong>Important:</strong> {test.important}</p>}
                  </div>
                </Card>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Testing Recommendations */}
        {triggers.recommendations && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-green-800 mb-4">{triggers.recommendations.title}</h2>
            <ol className="space-y-2">
              {triggers.recommendations.steps.map((step, i) => (
                <li key={i} className="text-green-900">{step}</li>
              ))}
            </ol>
          </Card>
        )}

        {/* Important Cautions */}
        {triggers.cautions && (
          <Card className="bg-red-50 border-red-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-red-800 mb-4">{triggers.cautions.title}</h2>
            <ul className="space-y-2">
              {triggers.cautions.items.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-red-900">
                  <span className="text-red-500 mt-0.5">⚠️</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Testing/Triggers page intro */}
      {triggers.intro && triggers.sections && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">{triggers.title || 'Testing'}</h2>
          <p className="text-[#57534E] leading-relaxed">{triggers.intro}</p>
        </Card>
      )}

      {/* Overview section */}
      {triggers.overview && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">{triggers.overview.title}</h2>
          <p className="text-[#57534E] leading-relaxed">{triggers.overview.description}</p>
        </Card>
      )}

      {/* Categories */}
      {triggers.categories?.map((category, idx) => (
        <SectionCard key={idx} title={category.name}>
          {category.description && <p className="text-[#57534E] mb-4">{category.description}</p>}
          {category.items && <BulletList items={category.items} />}
          {category.subCategories && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {category.subCategories.map((sub, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#2D2D2D] mb-2">{sub.name}</h4>
                  <BulletList items={sub.items} markerColor="text-secondary" />
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      ))}

      {triggers.common && (
        <SectionCard title="Common Triggers">
          <BulletList items={triggers.common} />
        </SectionCard>
      )}

      {triggers.environmental && (
        <SectionCard title="Environmental Triggers">
          <BulletList items={triggers.environmental} />
        </SectionCard>
      )}

      {/* Critical Facts section */}
      {triggers.criticalFacts && (
        <Card className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-4">{triggers.criticalFacts.title}</h2>
          <ul className="space-y-2">
            {triggers.criticalFacts.facts.map((fact, i) => (
              <li key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>{fact}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {triggers.note && (
        <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">Important Note</h2>
          <p className="text-amber-900">{triggers.note}</p>
        </Card>
      )}

      {/* Generic sections-based testing/triggers */}
      {triggers.sections?.map((section, idx) => (
        <SectionCard key={idx} title={section.name}>
          {section.description && <p className="text-[#57534E] mb-4">{section.description}</p>}
          {section.items && <BulletList items={section.items} />}
        </SectionCard>
      ))}
    </div>
  );
};

const SupplementsTab = ({ condition }) => {
  const supplements = condition.supplements;
  if (!supplements) return <p className="text-[#57534E]">No supplements information available.</p>;

  // Parasites-style supplements with dieOffWarning and detailed categories
  if (supplements.dieOffWarning) {
    return (
      <div className="space-y-6">
        <InspirationalQuote quotes={actionQuotes} variant="action" />

        {/* Intro */}
        {supplements.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <p className="text-[#57534E] leading-relaxed">{supplements.intro}</p>
          </Card>
        )}

        {/* Die-Off Warning */}
        <Card className="bg-red-50 border-red-300 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-red-800 mb-3">⚠️ {supplements.dieOffWarning.title}</h2>
          <p className="text-red-900 mb-3">{supplements.dieOffWarning.description}</p>
          <ul className="space-y-1 mb-4">
            {supplements.dieOffWarning.symptoms?.map((symptom, i) => (
              <li key={i} className="text-red-800 flex items-start gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{symptom}</span>
              </li>
            ))}
          </ul>
          <p className="text-red-900 font-semibold bg-red-100 p-3 rounded-lg">{supplements.dieOffWarning.management}</p>
        </Card>

        {/* Supplement Categories */}
        {supplements.categories?.map((category, idx) => (
          <div key={idx} className="space-y-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{category.name}</h2>
              {category.importance && (
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  category.importance === 'ESSENTIAL' || category.importance === 'CRITICAL' ? 'bg-red-100 text-red-700' :
                  category.importance === 'FOUNDATIONAL' || category.importance === 'HIGH' ? 'bg-amber-100 text-amber-700' :
                  'bg-stone-100 text-stone-700'
                }`}>{category.importance}</span>
              )}
            </div>
            {category.importanceNote && <p className="text-sm text-[#57534E] italic">{category.importanceNote}</p>}
            {category.binderNote && <p className="text-sm text-amber-700 font-medium bg-amber-50 p-3 rounded-lg">{category.binderNote}</p>}
            
            <div className="grid md:grid-cols-2 gap-4">
              {category.supplements?.map((item, i) => (
                <Card key={i} className={`${condition.lightColor || 'bg-stone-50'} rounded-xl border-0 p-5`}>
                  <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-2">{item.name}</h3>
                  {item.dose && <p className="text-sm text-primary font-medium mb-2">{item.dose}</p>}
                  {item.evidence && <p className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded inline-block mb-2">{item.evidence}</p>}
                  {item.benefits && (
                    <ul className="space-y-1 mb-3">
                      {item.benefits.map((benefit, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-[#57534E]">
                          <span className="text-green-500 mt-0.5">✓</span>
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.notes && <p className="text-xs text-[#A8A29E] bg-white/50 p-2 rounded">{item.notes}</p>}
                </Card>
              ))}
            </div>
          </div>
        ))}

        {/* Sample Protocol */}
        {supplements.sampleProtocol && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-green-800 mb-4">{supplements.sampleProtocol.title}</h2>
            <div className="space-y-4">
              {supplements.sampleProtocol.phases?.map((phase, i) => (
                <div key={i} className="bg-white/60 rounded-lg p-4">
                  <h3 className="font-bold text-green-700 mb-2">{phase.name}</h3>
                  <ul className="space-y-1">
                    {phase.items?.map((item, j) => (
                      <li key={j} className="text-green-900 flex items-start gap-2 text-sm">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Warnings */}
        {supplements.warnings && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">⚠️ Important Reminders</h2>
            <ul className="space-y-2">
              {supplements.warnings.map((warning, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-900">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // Extended MTHFR supplements format
  if (supplements.criticalWarning) {
    return (
      <div className="space-y-6">
        <InspirationalQuote quotes={actionQuotes} variant="action" />

        {/* Intro */}
        {supplements.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <p className="text-[#57534E] leading-relaxed font-medium">{supplements.intro}</p>
          </Card>
        )}

        {/* Critical Warning */}
        <Card className="bg-red-50 border-red-300 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-red-800 mb-3">⚠️ {supplements.criticalWarning.title}</h2>
          <p className="text-red-900 mb-3">{supplements.criticalWarning.description}</p>
          <ul className="space-y-1 mb-4">
            {supplements.criticalWarning.symptoms.map((symptom, i) => (
              <li key={i} className="text-red-800 flex items-start gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span>{symptom}</span>
              </li>
            ))}
          </ul>
          <p className="text-red-900 font-semibold bg-red-100 p-3 rounded-lg">{supplements.criticalWarning.solution}</p>
        </Card>

        {/* Supplement Items */}
        {supplements.items && (
          <div className="grid md:grid-cols-2 gap-4">
            {supplements.items.map((item, i) => (
              <SupplementCard key={i} item={item} lightColor={condition.lightColor} />
            ))}
          </div>
        )}

        {/* Building Protocol */}
        {supplements.buildingProtocol && (
          <Card className="bg-green-50 border-green-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-green-800 mb-4">{supplements.buildingProtocol.title}</h2>
            <ol className="space-y-2">
              {supplements.buildingProtocol.steps.map((step, i) => (
                <li key={i} className="text-green-900">{step}</li>
              ))}
            </ol>
          </Card>
        )}

        {/* Reaction Protocol */}
        {supplements.reactionProtocol && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-4">{supplements.reactionProtocol.title}</h2>
            <ul className="space-y-2">
              {supplements.reactionProtocol.steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-900">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InspirationalQuote quotes={actionQuotes} variant="action" />
      
      {supplements.disclaimer && (
        <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">⚠️ Important Disclaimer</h2>
          <p className="text-amber-900">{supplements.disclaimer}</p>
        </Card>
      )}

      {supplements.categories?.map((category, idx) => (
        <div key={idx} className="space-y-4">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{category.name}</h2>
          {category.description && <p className="text-[#57534E] mb-4">{category.description}</p>}
          <div className="grid md:grid-cols-2 gap-4">
            {category.items?.map((item, i) => (
              <SupplementCard key={i} item={item} lightColor={condition.lightColor} />
            ))}
          </div>
        </div>
      ))}

      {supplements.items && !supplements.categories && !supplements.criticalWarning && (
        <div className="grid md:grid-cols-2 gap-4">
          {supplements.items.map((item, i) => (
            <SupplementCard key={i} item={item} lightColor={condition.lightColor} />
          ))}
        </div>
      )}

      {supplements.avoidList && (
        <Card className="bg-red-50 border-red-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-red-700 mb-4">{supplements.avoidList.title || 'Supplements to Avoid'}</h2>
          <BulletList items={supplements.avoidList.items} markerColor="text-red-500" />
        </Card>
      )}

      {/* Critical Guidelines for Mold */}
      {supplements.criticalGuidelines && (
        <Card className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-4">{supplements.criticalGuidelines.title}</h2>
          <ul className="space-y-2">
            {supplements.criticalGuidelines.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

const LifestyleTab = ({ condition }) => {
  const lifestyle = condition.lifestyle;
  if (!lifestyle) return <p className="text-[#57534E]">No lifestyle information available.</p>;

  return (
    <div className="space-y-6">
      <InspirationalQuote quotes={actionQuotes} variant="action" />

      {/* Intro text */}
      {lifestyle.intro && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          <p className="text-[#57534E] leading-relaxed">{lifestyle.intro}</p>
        </Card>
      )}

      {lifestyle.categories?.map((category, idx) => (
        <div key={idx} className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{category.name}</h2>
            {category.priority && (
              <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                category.priority.includes('CRITICAL') ? 'bg-red-100 text-red-700' :
                category.priority.includes('HIGH') ? 'bg-amber-100 text-amber-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {category.priority}
              </span>
            )}
          </div>
          {category.description && <p className="text-[#57534E] mb-4">{category.description}</p>}
          
          {/* Handle array of strings (Mold style) */}
          {Array.isArray(category.items) && typeof category.items[0] === 'string' && (
            <Card className="bg-white border border-stone-200 rounded-xl p-5">
              <ul className="space-y-2">
                {category.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#57534E]">
                    <span className="text-primary mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          
          {/* Handle array of objects (other conditions) */}
          {Array.isArray(category.items) && typeof category.items[0] === 'object' && (
            <div className="grid md:grid-cols-2 gap-4">
              {category.items.map((item, i) => (
                <LifestyleCard key={i} item={item} />
              ))}
            </div>
          )}
        </div>
      ))}

      {lifestyle.items && !lifestyle.categories && (
        <div className="grid md:grid-cols-2 gap-4">
          {lifestyle.items.map((item, i) => (
            <LifestyleCard key={i} item={item} />
          ))}
        </div>
      )}

      {lifestyle.tips && (
        <SectionCard title="Quick Tips">
          <BulletList items={lifestyle.tips} markerColor="text-primary" />
        </SectionCard>
      )}

      {/* Hard Truth section for Mold */}
      {lifestyle.hardTruth && (
        <Card className="bg-stone-800 text-white rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold mb-4">{lifestyle.hardTruth.title}</h2>
          <ul className="space-y-2">
            {lifestyle.hardTruth.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-amber-400 mt-0.5">•</span>
                <span className="text-stone-200">{item}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

const DietTab = ({ condition }) => {
  const diet = condition.diet;
  if (!diet) return <p className="text-[#57534E]">No diet information available.</p>;

  // Extended MTHFR diet format
  if (diet.isExtendedMTHFR) {
    return (
      <div className="space-y-6">
        <InspirationalQuote quotes={actionQuotes} variant="action" />

        {/* Title & Intro */}
        {diet.intro && (
          <Card className="bg-stone-50 rounded-xl border-0 p-6">
            <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">{diet.title}</h2>
            <p className="text-[#57534E] leading-relaxed">{diet.intro}</p>
          </Card>
        )}

        {/* Foods to Include */}
        {diet.include && (
          <div className="space-y-4">
            <h2 className="text-xl font-nunito font-bold text-green-800 flex items-center gap-2">
              <span>{diet.include.icon}</span> {diet.include.title}
            </h2>
            {diet.include.categories?.map((category, idx) => (
              <Card key={idx} className="bg-green-50 border border-green-200 rounded-xl p-5">
                <h3 className="text-lg font-nunito font-bold text-green-800 mb-3">{category.name}</h3>
                <ul className="space-y-1.5">
                  {category.items?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-green-900">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )}

        {/* Foods to Avoid */}
        {diet.avoid && diet.avoid.categories && (
          <div className="space-y-4">
            <h2 className="text-xl font-nunito font-bold text-red-800 flex items-center gap-2">
              <span>{diet.avoid.icon}</span> {diet.avoid.title}
            </h2>
            {diet.avoid.categories?.map((category, idx) => (
              <Card key={idx} className="bg-red-50 border border-red-200 rounded-xl p-5">
                <h3 className="text-lg font-nunito font-bold text-red-800 mb-2">{category.name}</h3>
                {category.reason && (
                  <p className="text-sm text-red-700 mb-3 italic">{category.reason}</p>
                )}
                <ul className="space-y-1.5">
                  {category.items?.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-red-900">
                      <span className="text-red-500 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        )}

        {/* Sample Day */}
        {diet.sampleDay && (
          <SectionCard title={diet.sampleDay.title}>
            <div className="space-y-3">
              {diet.sampleDay.meals?.map((meal, i) => (
                <div key={i} className="bg-stone-50 rounded-lg p-4">
                  <h4 className="font-semibold text-[#2D2D2D] mb-1">{meal.name}</h4>
                  <p className="text-sm text-[#57534E]">{meal.description}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Tips */}
        {diet.tips && (
          <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
            <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{diet.tips.title}</h2>
            <ul className="space-y-2">
              {diet.tips.items?.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-amber-900">
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InspirationalQuote quotes={actionQuotes} variant="action" />

      {/* Intro text */}
      {diet.intro && (
        <Card className="bg-stone-50 rounded-xl border-0 p-6">
          <p className="text-[#57534E] leading-relaxed">{diet.intro}</p>
        </Card>
      )}

      {diet.overview && (
        <SectionCard title="Overview">
          <p className="text-[#57534E] leading-relaxed">{diet.overview}</p>
        </SectionCard>
      )}

      {diet.principles && (
        <HighlightCard title="Key Principles" lightColor={condition.lightColor}>
          <BulletList items={diet.principles} markerColor={condition.textColor} />
        </HighlightCard>
      )}

      {/* Foods to Emphasize (Mold style) */}
      {diet.foodsToEmphasize && (
        <div className="space-y-4">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{diet.foodsToEmphasize.title}</h2>
          {diet.foodsToEmphasize.categories?.map((category, idx) => (
            <Card key={idx} className="bg-green-50 border border-green-200 rounded-xl p-5">
              <h3 className="text-lg font-nunito font-bold text-green-800 mb-3">{category.name}</h3>
              <ul className="space-y-1.5">
                {category.items?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-green-900">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {category.note && (
                <p className="mt-3 text-sm text-green-700 italic">{category.note}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Foods Grid: Enjoy / Limit / Avoid - only when avoid is an array */}
      {(diet.enjoy || diet.limit || (diet.avoid && Array.isArray(diet.avoid))) && (
        <div className="grid md:grid-cols-3 gap-4">
          {diet.enjoy && Array.isArray(diet.enjoy) && (
            <DietCategoryCard 
              title="✓ Enjoy Freely" 
              bgColor="bg-green-50" 
              headerBg="bg-green-600" 
              textColor="text-white"
              items={diet.enjoy}
            />
          )}
          {diet.limit && Array.isArray(diet.limit) && (
            <DietCategoryCard 
              title="⚡ Limit / Moderate" 
              bgColor="bg-amber-50" 
              headerBg="bg-amber-500" 
              textColor="text-white"
              items={diet.limit}
            />
          )}
          {diet.avoid && Array.isArray(diet.avoid) && (
            <DietCategoryCard 
              title="✕ Avoid" 
              bgColor="bg-red-50" 
              headerBg="bg-red-600" 
              textColor="text-white"
              items={diet.avoid}
            />
          )}
        </div>
      )}

      {/* Foods to Avoid (Mold style with reasons) */}
      {diet.foodsToAvoid && (
        <div className="space-y-4">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">{diet.foodsToAvoid.title}</h2>
          {diet.foodsToAvoid.categories?.map((category, idx) => (
            <Card key={idx} className="bg-red-50 border border-red-200 rounded-xl p-5">
              <h3 className="text-lg font-nunito font-bold text-red-800 mb-2">{category.name}</h3>
              {category.reason && (
                <p className="text-sm text-red-700 mb-3 italic">{category.reason}</p>
              )}
              <ul className="space-y-1.5">
                {category.items?.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-red-900">
                    <span className="text-red-500 mt-0.5">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}

      {diet.categories?.map((category, idx) => (
        <SectionCard key={idx} title={category.name}>
          {category.description && <p className="text-[#57534E] mb-4">{category.description}</p>}
          {category.items && <BulletList items={category.items} />}
        </SectionCard>
      ))}

      {/* Sample Day (Mold style) */}
      {diet.sampleDay && (
        <SectionCard title={diet.sampleDay.title}>
          <div className="space-y-3">
            {diet.sampleDay.meals?.map((meal, i) => (
              <div key={i} className="bg-stone-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#2D2D2D] mb-1">{meal.name}</h4>
                <p className="text-sm text-[#57534E]">{meal.description}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {diet.mealIdeas && (
        <SectionCard title="Meal Ideas">
          <div className="grid md:grid-cols-2 gap-4">
            {diet.mealIdeas.map((meal, i) => (
              <div key={i} className="bg-stone-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#2D2D2D] mb-2">{meal.name}</h3>
                <BulletList items={meal.items} markerColor="text-secondary" />
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {diet.tips && (
        <Card className="bg-amber-50 border-amber-200 rounded-xl p-6">
          <h2 className="text-xl font-nunito font-bold text-amber-800 mb-3">{diet.tips.title || 'Helpful Tips'}</h2>
          <ul className="space-y-2">
            {(diet.tips.items || diet.tips).map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-amber-500 mt-0.5">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

const AutoimmuneTab = ({ condition }) => {
  const autoimmune = condition.autoimmune;
  if (!autoimmune) return <p className="text-[#57534E]">No autoimmune information available.</p>;

  return (
    <div className="space-y-6">
      <InspirationalQuote quotes={calmingQuotes} variant="calming" />

      {autoimmune.overview && (
        <SectionCard title="Understanding the Autoimmune Connection">
          <p className="text-[#57534E] leading-relaxed whitespace-pre-line">{autoimmune.overview}</p>
        </SectionCard>
      )}

      {autoimmune.sections?.map((section, idx) => (
        <SectionCard key={idx} title={section.title}>
          {section.content && <p className="text-[#57534E] mb-4">{section.content}</p>}
          {section.points && <BulletList items={section.points} />}
          {section.items && <InfoGrid items={section.items} columns={1} />}
        </SectionCard>
      ))}

      {autoimmune.labTests && (
        <SectionCard title="Common Lab Tests">
          <div className="grid md:grid-cols-2 gap-4">
            {autoimmune.labTests.map((test, i) => (
              <div key={i} className="bg-stone-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#2D2D2D] mb-1">{test.name}</h3>
                <p className="text-sm text-[#57534E]">{test.purpose}</p>
                {test.normal && <p className="text-xs text-[#A8A29E] mt-1">Normal: {test.normal}</p>}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

const RecoveryTab = ({ condition }) => {
  const recovery = condition.recovery;
  if (!recovery) return <p className="text-[#57534E]">No recovery information available.</p>;

  return (
    <div className="space-y-6">
      <InspirationalQuote quotes={actionQuotes} variant="action" />

      {recovery.overview && (
        <SectionCard title="The Path to Recovery">
          <p className="text-[#57534E] leading-relaxed">{recovery.overview}</p>
        </SectionCard>
      )}

      {recovery.phases && (
        <div className="space-y-4">
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D]">Recovery Phases</h2>
          {recovery.phases.map((phase, idx) => (
            <Card key={idx} className={`${idx === 0 ? 'bg-red-50 border-red-200' : idx === 1 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'} rounded-xl p-6`}>
              <h3 className="font-bold text-[#2D2D2D] mb-2">{phase.name}</h3>
              <p className="text-[#57534E] mb-4">{phase.description}</p>
              {phase.goals && <BulletList items={phase.goals} />}
            </Card>
          ))}
        </div>
      )}

      {recovery.treatmentOptions && (
        <SectionCard title="Treatment Options">
          <div className="space-y-4">
            {recovery.treatmentOptions.map((option, i) => (
              <div key={i} className="bg-stone-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#2D2D2D] mb-2">{option.name}</h3>
                <p className="text-sm text-[#57534E]">{option.description}</p>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {recovery.successStories && (
        <HighlightCard title="Hope for Recovery" lightColor={condition.lightColor}>
          <p className="text-[#57534E]">{recovery.successStories}</p>
        </HighlightCard>
      )}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ConditionDetail = () => {
  const { conditionSlug } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  
  const condition = conditionData[conditionSlug];
  
  // PDF Export function for MCAS Emergency Card
  const exportEmergencyCardPDF = () => {
    if (!condition?.emergencyQuickRef) return;
    
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;
    const redColor = [220, 38, 38];
    const darkGray = [55, 65, 81];
    const lightGray = [107, 114, 128];
    
    // Title
    doc.setFillColor(...redColor);
    doc.rect(0, 0, pageWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.title, pageWidth / 2, 12, { align: 'center' });
    doc.setFontSize(10);
    doc.text(condition.emergencyQuickRef.subtitle, pageWidth / 2, 19, { align: 'center' });
    
    y = 35;
    
    // Pre-Medication Protocol
    doc.setTextColor(...redColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.preMedicationProtocol.title, margin, y);
    y += 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...lightGray);
    doc.text(condition.emergencyQuickRef.preMedicationProtocol.subtitle, margin, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.preMedicationProtocol.steps.forEach((step, i) => {
      const lines = doc.splitTextToSize(`${i + 1}. ${step}`, pageWidth - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 4 + 1;
    });
    
    y += 5;
    
    // EpiPen Instructions
    doc.setTextColor(...redColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.epiPenInstructions.title, margin, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...redColor);
    doc.text('WHEN TO USE:', margin, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.epiPenInstructions.whenToUse.forEach((item) => {
      doc.text(`• ${item}`, margin + 3, y);
      y += 4;
    });
    
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...redColor);
    doc.text('HOW TO USE:', margin, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.epiPenInstructions.howToUse.forEach((step) => {
      doc.text(step, margin + 3, y);
      y += 4;
    });
    
    y += 5;
    
    // Emergency Med Kit
    doc.setTextColor(...redColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.emergencyMeds.title, margin, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.emergencyMeds.items.forEach((item) => {
      doc.text(`☐ ${item}`, margin, y);
      y += 4;
    });
    
    y += 5;
    
    // Tell ER Staff
    doc.setTextColor(...redColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.tellER.title, margin, y);
    y += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.tellER.items.forEach((item) => {
      const lines = doc.splitTextToSize(`→ ${item}`, pageWidth - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 4 + 1;
    });
    
    y += 5;
    
    // Emergency Contacts
    doc.setTextColor(...redColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(condition.emergencyQuickRef.emergencyContacts.title, margin, y);
    y += 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...lightGray);
    doc.text(condition.emergencyQuickRef.emergencyContacts.note, margin, y);
    y += 6;
    
    doc.setFontSize(10);
    doc.setFont('courier', 'normal');
    doc.setTextColor(...darkGray);
    condition.emergencyQuickRef.emergencyContacts.fields.forEach((field) => {
      doc.text(field, margin, y);
      y += 5;
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...lightGray);
    doc.text('Generated by Calm Pathways - For Emergency Reference Only', pageWidth / 2, 285, { align: 'center' });
    
    doc.save('MCAS-Emergency-Card.pdf');
  };
  
  if (!condition) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-[#2D2D2D] mb-4">Condition not found</h2>
        <Button onClick={() => navigate('/knowledge-base')} className="bg-primary text-white">
          Back to Knowledge Base
        </Button>
      </div>
    );
  }

  const baseTabs = [
    { id: 'about', label: 'About', icon: Info },
    { id: 'symptoms', label: 'Symptoms', icon: Activity },
    { id: 'triggers', label: condition.triggers?.tabLabel || 'Testing', icon: AlertTriangle },
    { id: 'supplements', label: 'Supplements', icon: Pill },
    { id: 'lifestyle', label: 'Lifestyle', icon: Heart },
    { id: 'diet', label: 'Diet', icon: Apple }
  ];

  let tabs = [...baseTabs];
  if (condition.autoimmune?.hasTab) {
    tabs.push({ id: 'autoimmune', label: 'Autoimmune Deep Dive', icon: Microscope });
  }
  if (condition.recovery?.hasTab) {
    tabs.push({ id: 'recovery', label: 'Path to Recovery', icon: Sparkles });
  }

  // Determine which quotes to show based on active tab
  const isActionTab = ['supplements', 'lifestyle', 'diet', 'recovery'].includes(activeTab);
  const showQuote = isActionTab || ['about', 'symptoms', 'triggers', 'autoimmune'].includes(activeTab);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'about':
        return <AboutTab condition={condition} onExportPDF={exportEmergencyCardPDF} />;
      case 'symptoms':
        return <SymptomsTab condition={condition} />;
      case 'triggers':
        return <TriggersTab condition={condition} />;
      case 'supplements':
        return <SupplementsTab condition={condition} />;
      case 'lifestyle':
        return <LifestyleTab condition={condition} />;
      case 'diet':
        return <DietTab condition={condition} />;
      case 'autoimmune':
        return <AutoimmuneTab condition={condition} />;
      case 'recovery':
        return <RecoveryTab condition={condition} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="condition-detail">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/knowledge-base')}
        variant="ghost"
        className="text-[#57534E] hover:text-primary"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Knowledge Base
      </Button>

      {/* Header */}
      <div className={`${condition.lightColor} rounded-xl p-8`}>
        <div className="flex items-center gap-4 mb-4">
          <div className={`w-12 h-12 ${condition.color} rounded-full flex items-center justify-center`}>
            <BookOpen className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
            {condition.name}
          </h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          {condition.description}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-stone-200 p-2 flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-lg px-4 py-2.5 flex items-center gap-2 transition-all duration-200 font-medium text-sm ${
                  isActive 
                    ? `${condition.color} text-white shadow-md` 
                    : 'bg-transparent text-[#57534E] hover:bg-stone-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Show calming quote for info tabs */}
          {['about', 'symptoms', 'triggers', 'autoimmune'].includes(activeTab) && (
            <InspirationalQuote quotes={calmingQuotes} variant="calming" />
          )}
          
          {renderTabContent()}
        </div>
      </div>

      {/* References Section */}
      {condition.references && (
        <SectionCard title="References & Resources">
          <div className="space-y-2">
            {condition.references.map((ref, i) => (
              <a
                key={i}
                href={ref.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                {ref.title}
              </a>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

export default ConditionDetail;
